'use client';

import { Button, Skeleton } from '@common/components';
import { SvgStarLine } from '@common/icon';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import { AuthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { useJejuDonationValidation } from '@/app/donation/_hooks/useJejuDonationValidation';
import { DONATION_ROUTES } from '@/constants';

import { CertificateCard } from './CertificateCard';

// 연속 기부 년차 계산: 가장 최근 기부년도부터 연속된 횟수만 계산
// 예: 25년 기부 → 1년차, 25+26년 연속 → 2년차, 25년+27년(26년 X) → 1년차
const calculateConsecutiveDonationYears = (donationYears: number[]) => {
  if (donationYears.length === 0) return 0;

  const uniqueYears = [...new Set(donationYears)].toSorted((a, b) => b - a);
  const startYear = uniqueYears[0];
  const breakIndex = uniqueYears.findIndex(
    (year, index) => year !== startYear - index,
  );

  return breakIndex === -1 ? uniqueYears.length : breakIndex;
};

export const JejuCertificateContent = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const { isDonatedToJeju } = useJejuDonationValidation({
    onFailed: () => {
      onClose();
    },
  });

  const { data: certificate } = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/home-donation/certificate'](),
    select: (data) => data.context,
    enabled: isDonatedToJeju,
  });

  const { data: donationHistory } = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/home-donation/history']({
      query: {
        page: 1,
        size: 100,
      },
    }),
    select: (data) => data.context?.contents,
    enabled: isDonatedToJeju,
  });

  // 완료된 기부 내역에서 년도 추출 후 연속 기부 년차 계산
  const donationYear = useMemo(() => {
    if (!donationHistory) {
      console.warn('[JejuCertificate] donationHistory is empty or undefined');

      return null;
    }

    const completedDonationYears = donationHistory
      .filter((donation) => donation.donationStatus === 'COMPLETED')
      .map((donation) => Number(donation.paidAt?.split('.')[0]));

    if (completedDonationYears.length === 0) {
      console.warn(
        '[JejuCertificate] No completed donations found',
        donationHistory,
      );
    }

    const consecutiveYears = calculateConsecutiveDonationYears(
      completedDonationYears,
    );

    return consecutiveYears > 0 ? consecutiveYears : null;
  }, [donationHistory]);

  return (
    <article
      className={cn('flex flex-col items-center pt-32', 'mobile-tablet:pt-0')}
    >
      {certificate ? (
        <>
          <CertificateCard
            year={donationYear ?? null}
            name={certificate.name}
          />

          <ul className="flex w-280 flex-col gap-8 px-12 py-32 [&>li>p]:font-600 [&>li>p]:text-Gray-800 [&>li>p]:typo-body3 [&>li>span]:font-400 [&>li>span]:text-Gray-600 [&>li>span]:typo-body3 [&>li]:flex [&>li]:items-center [&>li]:justify-between">
            <li>
              <p>발급일</p>
              <span>{dayjs(certificate.issuedDate).format('YYYY.MM.DD')}</span>
            </li>
            <li>
              <p>기간</p>
              <span>
                {dayjs(certificate.certificateIssueEndDate).format(
                  'YYYY.MM.DD',
                )}
              </span>
            </li>
            <li />
          </ul>
        </>
      ) : (
        <div>
          <Skeleton className="h-[406px] w-280 rounded-large" />
          <div className="flex h-128 flex-col justify-center gap-8">
            <Skeleton className="h-16 w-280" />
            <Skeleton className="h-16 w-280" />
          </div>
        </div>
      )}
      <Button
        theme="primary-line"
        dimensions="H48"
        className="mt-auto w-280 gap-6"
        asChild
      >
        <CustomLink
          href={DONATION_ROUTES.MY_JEJU_BENEFIT_PUBLIC.pathname}
          aria-label="제주 공영관광지 혜택 안내 페이지로 이동"
          onClick={onClose}
        >
          <SvgStarLine className="size-16 text-Gray-600" originalFill />
          혜택보기
        </CustomLink>
      </Button>
    </article>
  );
};
